import React, { useState, useEffect } from 'react';
import { ideaAPI } from '../services/api';
import UpgradeModal from '../components/UpgradeModal';

const PLATFORMS = ['YouTube','Instagram','Both','Twitter','LinkedIn','Other'];
const CATEGORIES = ['Tech','Lifestyle','Finance','Health','Education','Comedy','Vlog','Other'];
const EMPTY = { title:'', platform:'YouTube', category:'Tech', notes:'', used: false };

export default function IdeasPage() {
  const [ideas,   setIdeas]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [filter, setFilter]     = useState('ALL');
  const [saving, setSaving]     = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [niche, setNiche]       = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError]       = useState('');
  const [upgrade, setUpgrade]   = useState(null);

  const load = () => ideaAPI.getAll().then(r => setIdeas(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openAdd  = () => { setEditing(null); setForm(EMPTY); setShowForm(true); setError(''); };
  const openEdit = (i) => { setEditing(i); setForm(i); setShowForm(true); setError(''); };
  const closeForm = () => { setShowForm(false); setEditing(null); setError(''); setSuggestions([]); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Idea title is required');
    setSaving(true); setError('');
    try {
      if (editing) await ideaAPI.update(editing.id, form);
      else         await ideaAPI.create(form);
      await load(); closeForm();
    } catch (e) {
      const data = e.response?.data;
      if (data?.error === 'PLAN_LIMIT_REACHED') {
        closeForm();
        setUpgrade({ feature: data.feature, limit: data.limit });
      } else {
        setError('Failed to save.');
      }
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this idea?')) return;
    await ideaAPI.delete(id); load();
  };

  const handleToggleUsed = async (idea) => {
    await ideaAPI.update(idea.id, { ...idea, used: !idea.used }); load();
  };

  const handleAISuggest = async () => {
    if (!niche.trim()) return;
    setSuggesting(true);
    try {
      const r = await ideaAPI.aiSuggest(niche);
      setSuggestions(r.data.ideas || []);
    } catch (err) {
      // Backend returns 429 + { error: 'AI_DAILY_LIMIT', message: '...' } when the
      // per-day quota is exhausted. Surface that message directly so the user
      // knows what happened instead of seeing a generic "unavailable".
      if (err?.response?.status === 429 && err.response.data?.message) {
        setSuggestions([err.response.data.message]);
      } else {
        setSuggestions(['AI generations unavailable — try again later']);
      }
    }
    finally { setSuggesting(false); }
  };

  const addSuggestion = (text) => {
    setForm(f => ({ ...f, title: text }));
    setSuggestions([]);
  };

  const filtered = filter === 'ALL' ? ideas
    : filter === 'USED' ? ideas.filter(i => i.used)
    : ideas.filter(i => !i.used);

  if (loading) return <div style={{ padding: '2rem', color: 'var(--muted)' }}>Loading...</div>;

  if (upgrade) return <UpgradeModal feature={upgrade.feature} limit={upgrade.limit} onClose={() => setUpgrade(null)} />;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="page-title">💡 Idea Vault</div>
          <div className="page-subtitle">Capture every idea before it disappears</div>
        </div>
        <button className="btn btn-green" onClick={openAdd}>+ New Idea</button>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {[['ALL', 'All Ideas'], ['UNUSED', 'Not Used'], ['USED', 'Used']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className="btn btn-sm"
            style={{ background: filter === k ? 'var(--ink)' : 'var(--surface-2)', color: filter === k ? 'white' : 'var(--muted)', border: 'none' }}>
            {l} ({k === 'ALL' ? ideas.length : k === 'USED' ? ideas.filter(i => i.used).length : ideas.filter(i => !i.used).length})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>💡</div>
          <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>No ideas yet</div>
          <div style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>Start saving your content ideas here</div>
          <button className="btn btn-green" onClick={openAdd}>+ Add First Idea</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.85rem' }}>
          {filtered.map(i => (
            <div key={i.id} className="card" style={{ opacity: i.used ? 0.6 : 1, transition: 'all 0.15s' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', flex: 1, lineHeight: 1.4 }}>{i.title}</div>
                <button onClick={() => handleToggleUsed(i)}
                  style={{ padding: '0.25rem 0.5rem', borderRadius: 'var(--r-sm)', border: 'none', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, background: i.used ? 'var(--green-pale)' : 'var(--surface-2)', color: i.used ? 'var(--green)' : 'var(--muted)', flexShrink: 0 }}>
                  {i.used ? '✓ Used' : 'Mark Used'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {i.platform  && <span className="badge badge-blue">{i.platform}</span>}
                {i.category  && <span className="badge badge-gray">{i.category}</span>}
              </div>
              {i.notes && <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginBottom: '0.75rem', lineHeight: 1.5 }}>{i.notes}</div>}
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={() => openEdit(i)}>Edit</button>
                <button className="btn btn-sm" onClick={() => handleDelete(i.id)} style={{ background: 'var(--red-pale)', color: 'var(--red)', border: 'none' }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div onClick={closeForm} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 'var(--r-xl)', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflow: 'auto', padding: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.1rem' }}>{editing ? 'Edit Idea' : '+ New Idea'}</div>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
            </div>

            {/* AI Suggest */}
            {!editing && (
              <div style={{ background: 'var(--green-pale)', border: '1px solid rgba(26,122,74,0.15)', borderRadius: 'var(--r-lg)', padding: '1rem', marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--green)', marginBottom: '0.5rem' }}>✨ AI Idea Generator</div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input className="form-input" placeholder="Enter your niche (e.g. Tech, Cooking)" value={niche} onChange={e => setNiche(e.target.value)} style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAISuggest())} />
                  <button type="button" className="btn btn-green btn-sm" onClick={handleAISuggest} disabled={suggesting}>{suggesting ? '...' : 'Generate'}</button>
                </div>
                {suggestions.length > 0 && (
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {suggestions.map((s, i) => (
                      <div key={i} onClick={() => addSuggestion(s)}
                        style={{ padding: '0.5rem 0.75rem', background: 'white', borderRadius: 'var(--r-sm)', fontSize: '0.82rem', cursor: 'pointer', border: '1px solid rgba(26,122,74,0.2)' }}
                        onMouseOver={e => e.currentTarget.style.background = '#f0faf5'}
                        onMouseOut={e => e.currentTarget.style.background = 'white'}>
                        + {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Idea Title *</label>
                <input className="form-input" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Day in the life of a software engineer" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Platform</label>
                  <select className="form-input" value={form.platform} onChange={e => setForm(f => ({...f, platform: e.target.value}))}>
                    {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Category</label>
                  <select className="form-input" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Notes</label>
                <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Key points, references, angle..." style={{ resize: 'vertical' }} />
              </div>
              {error && <div style={{ padding: '0.65rem', background: 'var(--red-pale)', borderRadius: 'var(--r-md)', fontSize: '0.82rem', color: 'var(--red)' }}>⚠️ {error}</div>}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn btn-green" style={{ flex: 2 }} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Save Idea'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
